import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Save, Send } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { QuestionRenderer } from "@/components/lift/QuestionRenderer";
import { ModuleTitleLIFT } from "@/components/lift/ModuleTitleLIFT";
import LiftPacketPanel from "@/components/rhythm/LiftPacketPanel";
import { useAuth } from "@/contexts/AuthContext";

export default function SelfReflectionForm() {
  const { cycleId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [template, setTemplate] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [readonly, setReadonly] = useState(false);
  const [status, setStatus] = useState("draft");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [cycleName, setCycleName] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [c, sr] = await Promise.all([
          api.get(`/lift/cycles/${cycleId}`),
          api.get(`/lift/self-reflection/${cycleId}`),
        ]);
        setCycleName(c.data.name);
        setTemplate(sr.data.template || []);
        setAnswers(sr.data.answers || []);
        setReadonly(!!sr.data.readonly);
        setStatus(sr.data.status);
      } catch (err) {
        toast.error(formatApiErrorDetail(err?.response?.data?.detail));
      } finally {
        setLoading(false);
      }
    })();
  }, [cycleId]);

  const save = async (nextStatus) => {
    setSaving(true);
    try {
      await api.put(`/lift/self-reflection/${cycleId}`, {
        answers,
        status: nextStatus,
      });
      toast.success(nextStatus === "submitted" ? "Self-reflection submitted." : "Draft saved.");
      setStatus(nextStatus);
      if (nextStatus === "submitted") setReadonly(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const submit = async () => {
    if (!window.confirm("Submit your self-reflection? You will not be able to edit it after this.")) return;
    await save("submitted");
  };

  if (loading) return <p className="italic text-charcoal-muted">Loading…</p>;

  return (
    <div className="space-y-6" data-testid="lift-self-reflection">
      <Link to={`/lift/cycle/${cycleId}`} className="patria-btn-ghost -ml-3">
        <ChevronLeft className="h-4 w-4" strokeWidth={1.5} /> Back to cycle
      </Link>
      <div>
        <ModuleTitleLIFT />
        <h1 className="mt-3 text-4xl font-serif text-forest-900">Self-Reflection</h1>
        <p className="text-sm text-charcoal-muted">{cycleName}</p>
        {readonly && (
          <p className="mt-2 patria-chip-green inline-block">Submitted — read-only</p>
        )}
      </div>

      <div className="patria-card p-6 sm:p-8">
        <QuestionRenderer
          template={template}
          answers={answers}
          onChange={setAnswers}
          disabled={readonly || saving}
        />
      </div>

      {user?.id && <LiftPacketPanel cycleId={cycleId} employeeId={user.id} />}

      {!readonly && (
        <div className="flex justify-end gap-3">
          <button
            className="patria-btn-secondary"
            onClick={() => save("draft")}
            disabled={saving}
            data-testid="sr-save-draft"
          >
            <Save className="h-4 w-4" strokeWidth={1.5} /> Save draft
          </button>
          <button
            className="patria-btn-primary"
            onClick={submit}
            disabled={saving}
            data-testid="sr-submit"
          >
            <Send className="h-4 w-4" strokeWidth={1.5} /> Submit reflection
          </button>
        </div>
      )}
    </div>
  );
}
